import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import { GameContext, getLotteryRoutePath } from "Components/Context";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import LNG_MAP from "i18n/languages";

const REGION_LABELS = {
  americas: "header.lotterySelector.regions.americas",
  europe: "header.lotterySelector.regions.europe",
  africa: "header.lotterySelector.regions.africa",
  "asia-pacific": "header.lotterySelector.regions.asiaPacific",
};

const LotterySelector = () => {
  const { t, i18n } = useTranslation("common");
  const { selectedLotteryId, setSelectedLotteryId, supportedLotteries } = useContext(GameContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);

  const groupedLotteries = useMemo(() => {
    const groups = new Map();
    (supportedLotteries || []).forEach((lottery) => {
      const regionKey = lottery?.region || "americas";
      if (!groups.has(regionKey)) {
        groups.set(regionKey, []);
      }
      groups.get(regionKey).push(lottery);
    });
    return [...groups.entries()];
  }, [supportedLotteries]);

  const selectedLottery = useMemo(
    () => (supportedLotteries || []).find((lottery) => lottery.id === selectedLotteryId) || supportedLotteries?.[0],
    [selectedLotteryId, supportedLotteries]
  );

  const selectedLabel = selectedLottery
    ? t(selectedLottery.labelKey, { defaultValue: selectedLottery.id })
    : t("header.lotterySelector.selectLottery");

  const open = Boolean(anchorEl);

  const handleSelectLottery = (lotteryId) => {
    const selectedLottery = (supportedLotteries || []).find((lottery) => lottery.id === lotteryId);
    if (!selectedLottery) {
      setAnchorEl(null);
      return;
    }

    const localeFromPath = location.pathname.split("/")[1]?.toLowerCase();
    const locale = Object.prototype.hasOwnProperty.call(LNG_MAP, localeFromPath)
      ? localeFromPath
      : (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

    setSelectedLotteryId(lotteryId, { explicit: true });
    navigate(getLotteryRoutePath(locale, selectedLottery.id));
    setAnchorEl(null);
  };

  return (
    <Box>
      <Button
        id="lottery-selector-trigger"
        aria-label={t("header.lotterySelector.selectLotteryAria")}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<KeyboardArrowDownIcon />}
        sx={(theme) => ({
          borderRadius: "999px",
          px: 1.6,
          py: 0.4,
          minWidth: "fit-content",
          textTransform: "none",
          fontWeight: 600,
          color: "text.primary",
          border: `1px solid ${alpha(theme.palette.text.primary, 0.18)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.64),
          backdropFilter: "blur(6px)",
          "&:hover": {
            borderColor: alpha(theme.palette.text.primary, 0.3),
            bgcolor: alpha(theme.palette.background.paper, 0.85),
          },
          "&:focus-visible": {
            outline: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
            outlineOffset: 2,
          },
        })}
      >
        {selectedLabel}
      </Button>

      <Menu
        id="lottery-selector-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.8,
              minWidth: 250,
              maxWidth: "min(90vw, 360px)",
              maxHeight: "min(65vh, 420px)",
            },
          },
        }}
      >
        {groupedLotteries.map(([regionKey, lotteries]) => (
          <Box key={regionKey}>
            <ListSubheader
              disableSticky
              sx={{
                lineHeight: "28px",
                fontWeight: 700,
                letterSpacing:
                  "0.03em"
              }}
            >
              {t(REGION_LABELS[regionKey] || "header.lotterySelector.regions.americas")}
            </ListSubheader>
            {lotteries.map((lottery) => {
              const selected = lottery.id === selectedLotteryId;
              return (
                <MenuItem
                  key={lottery.id}
                  selected={selected}
                  onClick={() => handleSelectLottery(lottery.id)}
                  sx={{ py: 0.85 }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%"
                    sx={{ mx: 2 }}
                  >
                    <Typography variant="body2"
                      sx={{
                        fontWeight: selected ? 700 : 500
                      }}
                    >
                      {t(lottery.labelKey, { defaultValue: lottery.id })}
                    </Typography>
                    {selected ? <CheckIcon fontSize="small" /> : null}
                  </Stack>
                </MenuItem>
              );
            })}
          </Box>
        ))}
      </Menu>
    </Box>
  );
};

export default LotterySelector;
